// components/layout/UserHeader.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { workplaceApi } from "@/lib/api/workplaceApi";
import { User, LogOut, Settings, ChevronDown, Building2, LayoutGrid } from "lucide-react";
import { useLayoutMode, setLayoutMode, nextLayoutMode, LAYOUT_MODE_LABELS } from "@/components/layout/layoutConfig";

// Import shadcn/ui components
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationBell from "@/components/layout/NotificationBell";

// Card styles
import { NEXILINK_CARD_WHITE } from '@/lib/nexilink-card-styles';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import CONFIG from '@/lib/config';

const UserHeader: React.FC = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const layoutMode = useLayoutMode();
    const [imageError, setImageError] = useState(false);
    const [jobTitle, setJobTitle] = useState<string | null>(null);

    // For an employed candidate, show their job title (from their workplace)
    // instead of the generic "Kandidat" role label.
    useEffect(() => {
        if (user?.role !== 'candidate') return;
        let cancelled = false;
        workplaceApi.getMyWorkplace().then((w) => {
            if (!cancelled) setJobTitle(w.find((x) => x.title)?.title ?? null);
        });
        return () => { cancelled = true; };
    }, [user?.role]);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    // Get user initials
    const getUserInitials = () => {
        if (!user || !user.email) return "?";

        if (user.first_name && user.last_name) {
            return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }

        return user.email[0].toUpperCase();
    };

    // Get user display name
    const getDisplayName = () => {
        if (!user) return "Användare";
        if (user.first_name) {
            return `${user.first_name} ${user.last_name || ''}`.trim();
        }
        return user.email.split('@')[0];
    };

    // Get role badge variant and label
    const getRoleInfo = () => {
        if (user?.role === 'admin') {
            return { label: 'Administrator', variant: 'default' as const };
        }
        if (user?.role === 'employer') {
            return { label: 'Arbetsgivare', variant: 'secondary' as const };
        }
        return { label: jobTitle || 'Kandidat', variant: 'outline' as const };
    };

    if (!user) return null;

    const roleInfo = getRoleInfo();

    return (
        // Unified "badge" pill: the notification bell and the user identity live in
        // one rounded card, split by a hairline divider, so they read as a single
        // matched component instead of an orphaned bare icon next to a card.
        <div className={`flex items-stretch h-[54px] overflow-hidden ${NEXILINK_CARD_WHITE}`}>
        <NotificationBell />
        <div className="w-px bg-gray-100 self-stretch" />
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    className="h-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 hover:bg-gray-100 transition-colors outline-none"
                >
                    {/* Avatar */}
                    <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm">
                        <AvatarImage
                            src={!imageError ? resolveMediaUrl(user.picture_url) : undefined}
                            alt={getDisplayName()}
                            onError={() => setImageError(true)}
                        />
                        <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] font-semibold text-xs sm:text-sm">
                            {getUserInitials()}
                        </AvatarFallback>
                    </Avatar>

                    {/* Name & Chevron (Hidden on mobile, visible on tablet+) */}
                    <div className="hidden md:flex items-center gap-2">
                        <div className="text-left">
                            <p className="text-sm font-semibold text-gray-900 leading-tight">
                                {getDisplayName()}
                            </p>
                            <p className="text-xs text-gray-500 leading-tight">
                                {roleInfo.label}
                            </p>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                className="w-64 sm:w-72 rounded-2xl shadow-2xl"
                align="end"
                sideOffset={8}
            >
                {/* User Info Header */}
                <DropdownMenuLabel className="p-3 sm:p-4 bg-[#A4C3B2]/30">
                    <div className="flex items-center gap-2 sm:gap-3">
                        {/* Larger Avatar */}
                        <Avatar className="w-10 h-10 sm:w-12 sm:h-12 ring-2 ring-white shadow-sm">
                            <AvatarImage
                                src={!imageError ? resolveMediaUrl(user.picture_url) : undefined}
                                alt={getDisplayName()}
                            />
                            <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] font-semibold text-base sm:text-lg">
                                {getUserInitials()}
                            </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                                {getDisplayName()}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 truncate">
                                {user.email}
                            </p>
                            <div className="mt-1">
                                <Badge
                                    variant={roleInfo.variant}
                                    className="text-xs"
                                >
                                    {roleInfo.label}
                                </Badge>
                            </div>
                        </div>
                    </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                {/* My Profile */}
                <DropdownMenuItem
                    onClick={() => navigate("/dashboard/profile")}
                    className="cursor-pointer p-2 sm:p-3 m-2 rounded-xl"
                >
                    <div className="flex items-center gap-2 sm:gap-3 w-full">
                        <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#A4C3B2]/40 text-[#1A4D4F]">
                            <User className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="text-sm font-medium">Min Profil</p>
                            <p className="text-xs text-muted-foreground">
                                {user.role === 'employer' ? 'Din arbetsgivarprofil' : 'Din profil och preferenser'}
                            </p>
                        </div>
                    </div>
                </DropdownMenuItem>

                {/* Min arbetsplats — candidates who are employed (post-hire) */}
                {user.role === 'candidate' && jobTitle && (
                    <DropdownMenuItem
                        onClick={() => navigate("/dashboard/my-workplace")}
                        className="cursor-pointer p-2 sm:p-3 m-2 mt-0 rounded-xl"
                    >
                        <div className="flex items-center gap-2 sm:gap-3 w-full">
                            <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#A4C3B2]/40 text-[#1A4D4F]">
                                <Building2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="text-sm font-medium">Min arbetsplats</p>
                                <p className="text-xs text-muted-foreground">Din organisation och enhet</p>
                            </div>
                        </div>
                    </DropdownMenuItem>
                )}

                {/* Account Settings */}
                <DropdownMenuItem
                    onClick={() => navigate("/account-settings")}
                    className="cursor-pointer p-2 sm:p-3 m-2 mt-0 rounded-xl"
                >
                    <div className="flex items-center gap-2 sm:gap-3 w-full">
                        <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#A4C3B2]/40 text-[#1A4D4F]">
                            <Settings className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="text-sm font-medium">Kontoinställningar</p>
                            <p className="text-xs text-muted-foreground">Hantera ditt konto</p>
                        </div>
                    </div>
                </DropdownMenuItem>

                {/* TEST TOGGLE: cycle the layout shell (classic → sidebar-island →
                    island). DEV-only (#37) — hidden outside dev-mode; end users get
                    the pinned DEFAULT_LAYOUT_MODE. Remove this item (and the
                    layoutConfig mode block) once a winner is picked. */}
                {CONFIG.SHOW_DEV_TOOLS && (
                <DropdownMenuItem
                    onClick={() => setLayoutMode(nextLayoutMode(layoutMode))}
                    className="cursor-pointer p-2 sm:p-3 m-2 mt-0 rounded-xl"
                >
                    <div className="flex items-center gap-2 sm:gap-3 w-full">
                        <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#A4C3B2]/40 text-[#1A4D4F]">
                            <LayoutGrid className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="text-sm font-medium flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">dev</span>
                                Layout: {LAYOUT_MODE_LABELS[layoutMode]}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Byt till {LAYOUT_MODE_LABELS[nextLayoutMode(layoutMode)]}
                            </p>
                        </div>
                    </div>
                </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                {/* Logout */}
                <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer p-2 sm:p-3 m-2 rounded-xl focus:bg-red-50"
                >
                    <div className="flex items-center gap-2 sm:gap-3 w-full">
                        <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-red-100 text-red-600">
                            <LogOut className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-red-600">Logga ut</p>
                            <p className="text-xs text-red-500">Avsluta din session</p>
                        </div>
                    </div>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
        </div>
    );
};

export default UserHeader;